import { isDefined } from "@clipboard-health/util-ts";
import type { KnockGuideStep } from "@knocklabs/client";
import { useGuides, useKnockClient } from "@knocklabs/react";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { KnockErrorBoundary } from "@src/appV2/Knock/components/KnockErrorBoundary";
import { useKnockMarkAsSeen } from "@src/appV2/Knock/hooks/useKnockMarkAsSeen";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useOpenUrl } from "@src/mobile/utils/openUrl";
import { Platform } from "@src/mobile/utils/platform";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  type RecordRatingPromptEventInput,
  useRecordRatingPromptEvent,
} from "./api/useRecordRatingPromptEvent";
import {
  KNOCK_LAST_REVIEW_PROMPTED_AT_ATTRIBUTE,
  REVIEW_PROMPT_GUIDE_TYPE,
  REVIEW_PROMPT_SUPPORT_URL,
} from "./constants";
import { normalizeContent } from "./normalizeContent";
import { ReviewPromptGuideView, type ReviewPromptGuideViewContent } from "./ReviewPromptGuideView";
import { useRequestInAppReview } from "./useRequestInAppReview";
import { useReviewPromptCooldowns } from "./useReviewPromptCooldowns";
import { getRatingPromptSessionId } from "./utils/ratingPromptSessionId";

interface ReviewPromptItemProps {
  content: ReviewPromptGuideViewContent;
  guideKey: string;
  step: KnockGuideStep;
  workplaceId?: string;
  workplaceName?: string;
}

interface UseRecordPromptEventParams {
  guideKey: string;
  workplaceId?: string;
}

/**
 * Builds the first-party engagement-event recorder shared by the modal and
 * direct-fire paths. The idempotency key is per-session so the same display's
 * events dedupe server-side.
 */
function useRecordPromptEvent(params: UseRecordPromptEventParams) {
  const { guideKey, workplaceId } = params;
  const { mutate: recordEvent } = useRecordRatingPromptEvent();

  return useCallback(
    (eventType: RecordRatingPromptEventInput["eventType"], feedbackText?: string) => {
      // Without a workplaceId the server can't authorize the write; skip it.
      if (!isDefined(workplaceId)) {
        return;
      }

      recordEvent({
        promptKey: guideKey,
        eventType,
        workplaceId,
        feedbackText,
        context: {
          platform: Platform.platform,
          appVersion: __APP_VERSION__,
          locale: navigator.language,
        },
        occurredAt: new Date().toISOString(),
        idempotencyId: `${getRatingPromptSessionId()}:${guideKey}:${eventType}`,
      });
    },
    [recordEvent, guideKey, workplaceId]
  );
}

type RatePromptHistory = "loading" | "prompted" | "fresh";

/**
 * Reads `lastReviewPromptedAt` once on mount. Only the rate paths (modal "Rate
 * Us" and direct-fire) ever write it, so its presence means the user has been
 * sent to the native review sheet at least once — route them straight to
 * direct-fire and skip the modal. Absence means a first-timer, who gets the
 * modal. Fails open: a failed read resolves to "fresh" so an eligible user
 * still gets the modal rather than a blank screen. Mirrors the
 * `user.get()`-on-mount pattern in `useEmitReviewPromptAttributes`.
 */
function useRatePromptHistory(): RatePromptHistory {
  const knockClient = useKnockClient();
  const [history, setHistory] = useState<RatePromptHistory>("loading");

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const user = await knockClient.user.get();
        if (!cancelled) {
          setHistory(user[KNOCK_LAST_REVIEW_PROMPTED_AT_ATTRIBUTE] ? "prompted" : "fresh");
        }
      } catch (error) {
        logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
        if (!cancelled) {
          setHistory("fresh");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [knockClient]);

  return history;
}

function ReviewPromptItem(props: ReviewPromptItemProps) {
  const { content, guideKey, step, workplaceId, workplaceName } = props;
  const { requestInAppReview } = useRequestInAppReview();
  const { identifyDeclineCooldown, identifyDismissCooldown } = useReviewPromptCooldowns();
  const openUrl = useOpenUrl();
  const modalState = useModalState(ModalStatus.OPEN);
  const recordPromptEvent = useRecordPromptEvent({ guideKey, workplaceId });

  useKnockMarkAsSeen(step);

  // No path archives the guide. Archive is server-side terminal in Knock —
  // once `archived_at` is set the user is permanently excluded, which would
  // kill the willing-rater re-prompt and the decline/dismiss re-prompts. Every
  // path closes the dialog locally and gates future visibility through the
  // dashboard targeting rule + the cooldown attribute writes in
  // `useReviewPromptCooldowns`.

  const hasLoggedViewed = useRef<string>();
  useEffect(() => {
    if (hasLoggedViewed.current === guideKey) {
      return;
    }

    hasLoggedViewed.current = guideKey;
    logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_SHOWN, { guideKey, workplaceId, workplaceName });
    recordPromptEvent("shown");
  }, [guideKey, workplaceId, workplaceName, recordPromptEvent]);

  return (
    <ReviewPromptGuideView
      content={content}
      modalState={modalState}
      onYesClick={async () => {
        step.markAsInteracted({ metadata: { action: "yes" } });
        recordPromptEvent("yes");
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_YES_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        // Close the dialog before kicking off the native call so the user
        // doesn't see our dialog and the native sheet stacked. The native call
        // writes `lastReviewPromptedAt`; its presence is what routes this user
        // straight to the native sheet on later eligible moments.
        modalState.closeModal();
        await requestInAppReview();
      }}
      onNoClick={() => {
        // markAsInteracted defers to submit/dismiss so the engagement record
        // is written exactly once, with whatever feedback text we have.
        recordPromptEvent("no");
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_NO_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        // Cooldown + count are written here so a user who bails (closes the
        // app) before Submit/Cancel is still excluded from re-prompts.
        void identifyDeclineCooldown();
      }}
      onLaterClick={async () => {
        step.markAsInteracted({ metadata: { action: "later" } });
        recordPromptEvent("later");
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_LATER_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        modalState.closeModal();
        await identifyDismissCooldown();
      }}
      onFeedbackSubmit={(feedback) => {
        step.markAsInteracted({ metadata: { action: "no", feedback } });
        recordPromptEvent("feedback_submitted", feedback);
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_FEEDBACK_SUBMITTED, {
          guideKey,
          workplaceId,
          workplaceName,
          feedback,
          feedbackLength: feedback.length,
        });
        // Closing is deferred to the appreciation step (onAppreciationClose) so
        // the user sees a thank-you before the dialog dismisses.
      }}
      onAppreciationClose={() => {
        // The submission was already recorded on submit; this only dismisses the
        // thank-you dialog. Must not re-fire any engagement event.
        modalState.closeModal();
      }}
      onFeedbackDismiss={() => {
        // Declined without elaborating — still counts as a decline; cooldown
        // was already written on the No tap.
        step.markAsInteracted({ metadata: { action: "no" } });
        recordPromptEvent("feedback_dismissed");
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_FEEDBACK_DISMISSED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        modalState.closeModal();
      }}
      onContactSupport={async () => {
        // The in-app browser opens over the SPA; the dialog stays mounted
        // underneath so the user can still pick Submit / Cancel when they
        // return. Don't archive, don't write any cooldown attribute.
        recordPromptEvent("contact_support");
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_CONTACT_SUPPORT_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        await openUrl(REVIEW_PROMPT_SUPPORT_URL);
      }}
    />
  );
}

/**
 * Returning-rater path: the user has already been sent to the native review
 * sheet before, so skip the modal and request it directly on mount. Renders
 * nothing.
 */
function ReviewPromptDirectFire(props: ReviewPromptItemProps) {
  const { guideKey, step, workplaceId, workplaceName } = props;
  const { requestInAppReview } = useRequestInAppReview();
  const recordPromptEvent = useRecordPromptEvent({ guideKey, workplaceId });

  const hasFired = useRef(false);
  useEffect(() => {
    if (hasFired.current) {
      return;
    }

    hasFired.current = true;
    logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_SHOWN, {
      guideKey,
      workplaceId,
      workplaceName,
      directFire: true,
    });
    recordPromptEvent("shown");
    step.markAsInteracted({ metadata: { action: "rate_direct" } });
    // No user gesture and the surface re-mounts every eligible session, so
    // advance the cooldown even when the native call throws — otherwise a
    // failing call would re-fire on every mount.
    void requestInAppReview({ advanceCooldownOnAttempt: true });
  }, [guideKey, workplaceId, workplaceName, step, recordPromptEvent, requestInAppReview]);

  return null;
}

function ReviewPromptEligibleItem(props: ReviewPromptItemProps) {
  const history = useRatePromptHistory();

  // Render nothing until the read resolves, so a returning rater never sees the
  // modal flash before the direct-fire decision is made. One round-trip, paid
  // only on the eligible path (a guide was already delivered to this user).
  if (history === "loading") {
    return null;
  }

  if (history === "prompted") {
    return <ReviewPromptDirectFire {...props} />;
  }

  return <ReviewPromptItem {...props} />;
}

interface ReviewPromptGuideInnerProps {
  workplaceId?: string;
  workplaceName?: string;
}

function ReviewPromptGuideInner(props: ReviewPromptGuideInnerProps) {
  const { workplaceId, workplaceName } = props;
  // Kill-switch gate at the root of the surface so the dialog never mounts
  // when the flag is off — even if a Knock dashboard guide somehow targets
  // through. The hook holds the belt-and-suspenders check for the native call.
  const isEnabled = useCbhFlag(CbhFeatureFlag.WORKPLACE_RATING_PROMPT, { defaultValue: false });
  const { guides } = useGuides({ type: REVIEW_PROMPT_GUIDE_TYPE });

  if (!isEnabled || guides.length === 0) {
    return null;
  }

  const guide = guides[0];
  const step = guide?.getStep();
  const content = normalizeContent(step?.content);

  if (!guide || !step || !content) {
    return null;
  }

  return (
    // Keyed on guide.key so the willing-rater read and the view's internal
    // step + feedback-text state reset if a different Knock guide takes the
    // slot mid-session.
    <ReviewPromptEligibleItem
      key={guide.key}
      content={content}
      guideKey={guide.key}
      step={step}
      workplaceId={workplaceId}
      workplaceName={workplaceName}
    />
  );
}

interface ReviewPromptGuideProps {
  /**
   * Active workplace (facility) id and display name, sourced by the parent
   * container from the session's active facility profile (`profile.userId` /
   * `profile.name`). `workplaceId` is sent to the server-side event log and is
   * authorized on the backend via `worksAt`, so it must be the workplace the
   * user is acting in — not the workplace-user id (sending the latter 403s).
   */
  workplaceId?: string;
  workplaceName?: string;
}

/**
 * App-root rating-prompt surface. Filters Knock guides on the custom
 * `app-store-review-soft-prompt` message type. Users who have already opted to
 * rate are sent straight to the native review sheet; everyone else gets the
 * modal, where "Rate Us" requests the native sheet, "No thanks" transitions to
 * a feedback step (textarea + Submit / Contact support / Cancel), and "Ask me
 * later" defers. Decline, dismiss, and rate each write a Knock user attribute
 * the dashboard uses to gate re-delivery. Contact support opens the support URL
 * in the in-app browser and leaves the dialog mounted underneath.
 */
export function ReviewPromptGuide(props: ReviewPromptGuideProps) {
  const { workplaceId, workplaceName } = props;
  return (
    <KnockErrorBoundary>
      <ReviewPromptGuideInner workplaceId={workplaceId} workplaceName={workplaceName} />
    </KnockErrorBoundary>
  );
}
